import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { toast } from 'react-toastify';
import { Button, Tooltip } from 'antd';
import { DownloadOutlined, UploadOutlined, HistoryOutlined } from '@ant-design/icons';
import { DIGITAL_PRODUCTS } from '../../models/digitalProducts';
import IncidentLessonsUploader from '../IncidentLessonsUploader';
import { createIncidentLog, getUserFullName, ACTIVITY_TYPES } from '../../utils/activityLogger';

const LessonsLearnedTab = ({ incidentId, incident }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [lessonsDocument, setLessonsDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Check if user has edit permissions (any CMT member)
  const canEdit = currentUser?.role && [
    'cmt_leader', 'cmt_leader_alt', 'cmt_admin', 'cmt_member',
    'crisis_manager', 'safety_officer', 'comms_media', 'legal_compliance'
  ].includes(currentUser.role);

  useEffect(() => {
    if (!incidentId || !currentUser?.orgId) {
      setLoading(false);
      return;
    }

    console.log('📚 LessonsLearnedTab: Setting up listener for incident:', incidentId);

    const lessonsRef = collection(db, 'c3_incident_lessons_learned');
    const q = query(
      lessonsRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', currentUser.orgId)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        console.log('📚 LessonsLearnedTab: Received update, docs:', snapshot.docs.length);
        
        if (snapshot.docs.length > 0) {
          // Take the most recent document (should only be one per incident)
          const doc = snapshot.docs[0];
          setLessonsDocument({
            id: doc.id,
            ...doc.data()
          });
        } else {
          setLessonsDocument(null);
        }
        
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('❌ LessonsLearnedTab: Error fetching lessons document:', error);
        setError('Failed to load lessons learned document');
        setLoading(false);
        toast.error('Failed to load lessons learned document');
      }
    );

    return () => {
      console.log('📚 LessonsLearnedTab: Cleaning up listener');
      unsubscribe();
    };
  }, [incidentId, currentUser?.orgId, db]);

  const createLessonsLearnedDocument = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to create lessons learned documents');
      return;
    }

    setCreating(true);
    console.log('📚 Creating lessons learned document for incident:', incidentId);
    console.log('📚 Current user debug:', {
      uid: currentUser?.uid,
      orgId: currentUser?.orgId,
      role: currentUser?.role,
      email: currentUser?.email
    });

    try {
      // Get the master template from digital_products
      const templateRef = doc(db, 'digital_products', 'post_incident_review_template');
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        throw new Error('Lessons learned template not found');
      }

      const templateData = templateDoc.data();
      console.log('📚 Retrieved template data:', templateData);

      if (!templateData.downloadURL) {
        throw new Error('Template PDF URL not found');
      }

      // Create incident-specific document record
      const lessonsRef = collection(db, 'c3_incident_lessons_learned');
      const docData = {
        incidentId,
        orgId: currentUser.orgId,
        originalProductId: 'post_incident_review_template',
        downloadURL: templateData.downloadURL, // Start with template URL
        fileName: `Lessons_Learned_${incident?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'Incident'}_${new Date().toISOString().split('T')[0]}.pdf`,
        version: 1,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        lastModifiedAt: serverTimestamp(),
        lastModifiedBy: currentUser.uid,
        isTemplate: true // Flag to indicate this is still the template, not a customized version
      };

      console.log('📚 Creating document with data:', docData);
      console.log('📚 Collection reference:', lessonsRef);
      console.log('📚 Database instance:', db);
      
      await addDoc(lessonsRef, docData);
      
      // FORENSIC LOGGING: Log lessons learned document creation
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: currentUser.orgId,
          action: ACTIVITY_TYPES.LESSONS_LEARNED_CREATED,
          details: `Lessons learned document created: "${docData.fileName}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            fileName: docData.fileName,
            templateUsed: 'post_incident_review_template',
            version: docData.version,
            isTemplate: docData.isTemplate
          }
        });
      } catch (logError) {
        console.error('Failed to log lessons learned creation:', logError);
      }
      
      toast.success('Lessons learned document created successfully');
      console.log('✅ Lessons learned document created');
    } catch (error) {
      console.error('❌ Error creating lessons learned document:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      toast.error(`Failed to create lessons learned document: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleUploadComplete = async (fileDetails) => {
    console.log('📚 Upload completed:', fileDetails);
    
    try {
      // Update the document with the new file details
      if (lessonsDocument) {
        const docRef = doc(db, 'c3_incident_lessons_learned', lessonsDocument.id);
        await updateDoc(docRef, {
          downloadURL: fileDetails.downloadURL,
          fileName: fileDetails.fileName,
          version: (lessonsDocument.version || 1) + 1,
          lastModifiedAt: serverTimestamp(),
          lastModifiedBy: currentUser.uid,
          isTemplate: false // Now it's a customized version
        });
        
        // FORENSIC LOGGING: Log lessons learned document update
        try {
          const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
          await createIncidentLog({
            incidentId,
            orgId: currentUser.orgId,
            action: ACTIVITY_TYPES.LESSONS_LEARNED_UPDATED,
            details: `Lessons learned document updated: "${fileDetails.fileName}" (v${(lessonsDocument.version || 1) + 1})`,
            userId: currentUser.uid,
            userEmail: currentUser.email,
            userFullName,
            priority: 'normal',
            metadata: {
              fileName: fileDetails.fileName,
              previousVersion: lessonsDocument.version || 1,
              newVersion: (lessonsDocument.version || 1) + 1,
              fileSize: fileDetails.size,
              isTemplate: false
            }
          });
        } catch (logError) {
          console.error('Failed to log lessons learned update:', logError);
        }
        
        toast.success('Lessons learned document updated successfully');
      }
    } catch (error) {
      console.error('❌ Error updating lessons learned document:', error);
      toast.error('Failed to update document record');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading lessons learned...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  // If no document exists, show create button
  if (!lessonsDocument) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <h4>📚 Lessons Learned</h4>
          <p className="text-muted">
            No lessons learned document has been created for this incident yet.
          </p>
          <p className="text-muted">
            Create a lessons learned document to capture insights and improvements from this incident.
          </p>
        </div>
        {canEdit && (
          <Button
            type="primary"
            size="large"
            loading={creating}
            onClick={createLessonsLearnedDocument}
            style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
          >
            📝 Create Lessons Learned Document
          </Button>
        )}
        {!canEdit && (
          <div className="alert alert-info">
            Only CMT members can create lessons learned documents.
          </div>
        )}
      </div>
    );
  }

  // Show the PDF viewer (similar to My Crisis Resources)
  return (
    <div className="lessons-learned-viewer" style={{ height: 'calc(100vh - 200px)' }}>
      <div className="card h-100">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">📚 Lessons Learned Document</h5>
              <small className="text-muted">
                Version {lessonsDocument.version} • 
                Last modified: {lessonsDocument.lastModifiedAt?.toDate?.()?.toLocaleString() || 'Unknown'}
                {lessonsDocument.isTemplate && (
                  <span className="badge bg-warning text-dark ms-2">Template - Not Customized</span>
                )}
              </small>
            </div>
            <div className="d-flex gap-2">
              {canEdit && (
                <IncidentLessonsUploader
                  incidentId={incidentId}
                  orgId={currentUser.orgId}
                  onUploadComplete={handleUploadComplete}
                />
              )}
              {lessonsDocument.downloadURL && (
                <Tooltip title="Download current version">
                  <Button
                    icon={<DownloadOutlined />}
                    href={lessonsDocument.downloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundColor: '#39325b', color: 'white' }}
                  >
                    Download
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <div className="card-body p-0 h-100">
          {lessonsDocument.downloadURL ? (
            <iframe
              src={`${lessonsDocument.downloadURL}#toolbar=0&navpanes=0`}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                backgroundColor: 'white'
              }}
              title="Lessons Learned PDF"
            />
          ) : (
            <div className="alert alert-warning m-3">
              PDF not available for this lessons learned document.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonsLearnedTab; 